import clsx from "clsx";
import React from "react";
import Image from "next/image";

function pushCtaClickDataLayer(dataLayerPayload, cta_button) {
  if (typeof window === "undefined" || !dataLayerPayload) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ...dataLayerPayload,
    cta_button: cta_button ?? "",
  });
}

function IconText(props) {
  const {
    textWrapperClassName,
    iconUrl,
    iconUrlWrapperClassName,
    iconUrlClassName,
    icon,
    text,
    wrapperClassName,
    iconPosition = "left",
    iconClassName,
    textClassName,
    subTextClassName,
    iconSize = "small",
    iconVariant = "default",
    subText = "",
    gap = "default",
    alignment = "start",
    responsive = true,
    onClick,
    href,
    target,
    role,
    title,
    dataLayerPayload = null,
    ctaButtonStable,
    "aria-label": ariaLabel,
    "aria-describedby": ariaDescribedby,
    ...restProps
  } = props;

  const handleClick = (e) => {
    if (dataLayerPayload) {
      pushCtaClickDataLayer(dataLayerPayload, ctaButtonStable ?? text);
    }
    onClick?.(e);
  };

  // Enhanced icon size options with more flexibility
  const ICON_SIZE_CLASS = {
    xs: "h-[24px] w-[24px]",
    sm: "h-[32px] w-[32px]",
    small: "h-[36px] w-[36px] md:h-[48px] md:w-[48px]",
    medium: "h-[48px] w-[48px] md:h-[56px] md:w-[56px]",
    large: "h-[56px] w-[56px] md:h-[72px] md:w-[72px]",
    xl: "h-[72px] w-[72px] md:h-[96px] md:w-[96px]",
  };

  // Gap options for better spacing control
  const GAP_CLASS = {
    none: "gap-0",
    xs: "gap-1",
    sm: "gap-2",
    default: "gap-4",
    lg: "gap-6",
    xl: "gap-8",
  };

  // Alignment options
  const ALIGNMENT_CLASS = {
    start: "items-start",
    center: "items-center",
    end: "items-end",
    stretch: "items-stretch",
  };

  // Icon variant styles
  const ICON_VARIANT_CLASS = {
    default: "bg-background-global",
    transparent: "bg-transparent",
    outline: "bg-transparent border border-gray-300",
    filled: "bg-primary text-white",
    gradient: "bg-gradient-to-r from-primary to-secondary text-white",
  };

  // Position-based layout classes
  const POSITION_CLASS = {
    left: "flex-row",
    right: "flex-row-reverse",
    top: "flex-col",
    bottom: "flex-col-reverse",
  };

  const sizeClass = typeof iconSize === "string" ? ICON_SIZE_CLASS[iconSize] : iconSize;
  const gapClass = GAP_CLASS[gap];
  const alignmentClass = ALIGNMENT_CLASS[alignment];
  const positionClass = POSITION_CLASS[iconPosition];
  const iconVariantClass = ICON_VARIANT_CLASS[iconVariant];

  const WrapperComponent = href ? "a" : onClick ? "button" : "div";

  // SEO için title attribute'u: önce title prop'u, sonra text, sonra aria-label
  const seoTitle = title || text || ariaLabel;

  return (
    <WrapperComponent
      className={clsx(
        "flex",
        positionClass,
        gapClass,
        alignmentClass,
        iconPosition === "left" && responsive && "flex-col sm:flex-row",
        iconPosition === "top" && responsive && "sm:flex-col",
        iconPosition === "bottom" && responsive && "sm:flex-col-reverse",
        iconPosition === "right" && responsive && "sm:flex-row-reverse",
        wrapperClassName,
      )}
      onClick={dataLayerPayload ? handleClick : onClick}
      href={href}
      target={target}
      role={role}
      title={seoTitle}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedby}
      {...restProps}
    >
      {icon && !iconUrl && (
        <span
          className={clsx(
            "flex shrink-0 items-center justify-center",
            "rounded-[6px]",
            sizeClass,
            iconVariantClass,
            iconClassName,
          )}
        >
          {icon}
        </span>
      )}
      {iconUrl && (
        <span
          className={clsx(
            "relative shrink-0 h-[72px] w-[72px]",
            iconUrlWrapperClassName
          )}
        >
          <Image
            fill
            src={iconUrl}
            alt={text || ariaLabel || ""}
            className={clsx("h-full w-full object-contain", iconUrlClassName)}
          />
        </span>
      )}
      <div className={clsx("flex flex-col gap-1 min-w-0 flex-1", textWrapperClassName)}>
        {text && (
          <span
            className={clsx(
              "text-lg leading-[26px] font-medium",
              textClassName,
            )}
          >
            {text}
          </span>
        )}
        {subText && (
          <span
            className={clsx(
              "leading-[21px] font-normal text-gray-600",
              subTextClassName,
            )}
          >
            {subText}
          </span>
        )}
      </div>
    </WrapperComponent>
  );
}

export default IconText;
